import React from 'react';

import { Alert, Modal } from 'antd-v5';
import { MdInfo, MdLock } from 'react-icons/md';
import { FormattedHTMLMessage, FormattedMessage } from 'react-intl';
import { Text } from 'scouting-ui/lib';
import { cssVars } from 'scouting-ui/lib/variables';

import type { ImportMBFileUploadOptions } from '@appTypes/esb';

import { ACCEPT_VENDOR_TYPES } from '../../constants';
import type { CampImport, VendorImport } from '../../hooks';
import type { ZoneKind } from '../../types';
import CampImportSetup from '../CampImportSetup';
import CampImportStatus from '../CampImportStatus';
import ImportDropzone from '../ImportDropzone';
import ImportErrorsModal from '../ImportErrorsModal';
import ImportStats from '../ImportStats';
import ImportStatusList from '../ImportStatusList';
import styles from './AdvancementImportModal.module.scss';

export interface AdvancementImportModalViewProps {
  open: boolean;
  onClose: () => void;
  /** The zone the user picked this session; the other zone is hidden */
  sessionKind: ZoneKind | null;
  vendor: VendorImport;
  camp: CampImport;
  /** Camp file picked but not uploaded until the user confirms the options */
  stagedCampFile: File | null;
  onVendorSelect: (file: File) => void;
  /** Stages the camp file; `onCampImport` uploads it */
  onCampSelect: (file: File) => void;
  onCampImport: (options: ImportMBFileUploadOptions) => void;
  onCampDiscard: () => void;
  onVendorCancel: () => void;
  onCampCancel: () => void;
}

const AdvancementImportModalView: React.FC<
  AdvancementImportModalViewProps
> = props => {
  const {
    open,
    onClose,
    sessionKind,
    vendor,
    camp,
    stagedCampFile,
    onVendorSelect,
    onCampSelect,
    onCampImport,
    onCampDiscard,
    onVendorCancel,
    onCampCancel,
  } = props;

  const showVendor = sessionKind !== 'camp';
  const showCamp = sessionKind !== 'vendor';

  return (
    <React.Fragment>
      <Modal
        open={open}
        onCancel={onClose}
        footer={false}
        width={780}
        destroyOnClose
        title={<FormattedMessage id="advancementImport.title" />}
        // Set padding explicitly: bundled antd v4 (bsa-ui) and v5 collide on
        // `.ant-modal-content`, which can strip v5's default content padding.
        styles={{ content: { padding: '20px 24px' } }}
      >
        <div className={styles.zones}>
          {showVendor && (
            <section className={styles.zone}>
              <Text>
                <FormattedMessage id="advancementImport.vendor.intro" />
              </Text>
              {vendor.isActive ? (
                <div className={styles.status}>
                  <ImportStatusList
                    uploading={vendor.uploading}
                    uploadedFiles={vendor.uploadedFiles}
                    uploadError={vendor.uploadError}
                    selectedFile={vendor.selectedFile}
                    errorsCount={vendor.errorDesc.length}
                    onCancel={onVendorCancel}
                    onRetry={vendor.retry}
                    onShowErrors={vendor.showErrors}
                  />
                  <ImportStats
                    uploadedCount={vendor.uploadedFiles.length}
                    onNavigateToHistory={() => {
                      onClose();
                      vendor.navigateToHistory();
                    }}
                  />
                </div>
              ) : (
                <div className={styles.card}>
                  <ImportDropzone
                    accept={ACCEPT_VENDOR_TYPES}
                    dropLabel={
                      <FormattedMessage id="advancementImport.vendor.dragHere" />
                    }
                    onSelectFile={onVendorSelect}
                  />
                </div>
              )}
            </section>
          )}

          {showCamp && (
            <section className={styles.zone}>
              <Text>
                <FormattedMessage id="advancementImport.camp.intro" />
              </Text>
              {camp.unauthorized ? (
                <Alert
                  type="error"
                  showIcon
                  message={<FormattedMessage id="shared.error" />}
                  description={<FormattedMessage id="shared.apiUnauthorized" />}
                />
              ) : camp.lockedByOther ? (
                <Alert
                  type="warning"
                  showIcon
                  icon={<MdLock />}
                  message={
                    <FormattedMessage id="advancementImport.camp.locked.title" />
                  }
                  description={
                    <FormattedMessage id="advancementImport.camp.locked.description" />
                  }
                />
              ) : camp.isActive ? (
                <React.Fragment>
                  <div className={styles.notice}>
                    <MdInfo
                      className={styles.noticeIcon}
                      color={cssVars['color-info']}
                    />
                    <Text font="body-caption">
                      <FormattedMessage id="advancementImport.camp.processingTime" />
                    </Text>
                  </div>
                  <CampImportStatus
                    view={camp.view}
                    fileName={camp.fileName}
                    uploadError={camp.uploadError}
                    cancelling={camp.cancelling}
                    isPrevious={camp.isPrevious}
                    summaryReport={camp.summaryReport}
                    errorRows={camp.errorRows}
                    errorCount={camp.errorCount}
                    errorsLoading={camp.errorsLoading}
                    starting={camp.starting}
                    onStart={camp.start}
                    onCancel={onCampCancel}
                  />
                </React.Fragment>
              ) : (
                <CampImportSetup
                  fileName={stagedCampFile?.name}
                  onSelectFile={onCampSelect}
                  onDiscard={onCampDiscard}
                  onImport={onCampImport}
                />
              )}
            </section>
          )}
        </div>

        <div className={styles.footer}>
          <Text font="body-caption">
            <FormattedHTMLMessage id="advancementImport.assistance" />
          </Text>
        </div>
      </Modal>
      <ImportErrorsModal
        open={vendor.errorsModalOpen}
        errorDesc={vendor.errorDesc}
        onClose={vendor.closeErrors}
      />
    </React.Fragment>
  );
};

export default AdvancementImportModalView;
